import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { getBlogPosts, deleteBlogPost } from '../../api/api';
import Loader from '../../components/Loader/Loader';
import BlogModal from '../../components/BlogModal/BlogModal';
import { useAuthStore } from '../../store/authStore';
import '../Animation.css';
import './Blog.css';

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPost, setSelectedPost] = useState(null);
  const [isEditingMode, setIsEditingMode] = useState(false);
  const { isAuthenticated } = useAuthStore();

  useEffect(() => {
    const fetchPosts = async () => {
      setLoading(true);
      try {
        const data = await getBlogPosts();
        setPosts(data);
      } catch (err) {
        console.error('Błąd pobierania wpisów blogowych:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
  }, []);

  // blokada scrolla przy otwartym modalu
  useEffect(() => {
    if (selectedPost || isEditingMode) {
      document.body.classList.add('modal-open');
    } else {
      document.body.classList.remove('modal-open');
    }

    return () => {
      document.body.classList.remove('modal-open');
    };
  }, [selectedPost, isEditingMode]);

  const openModal = (post = null, edit = false) => {
    setSelectedPost(post);
    setIsEditingMode(edit);
  };

  const closeModal = () => {
    setSelectedPost(null);
    setIsEditingMode(false);
  };

  const handlePostSaved = (savedPost) => {
    setPosts((prev) => {
      if (savedPost.id) {
        return prev.map((p) => (p.id === savedPost.id ? savedPost : p));
      } else {
        return [...prev, savedPost];
      }
    });
    setSelectedPost(savedPost);
    setIsEditingMode(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Na pewno chcesz usunąć ten wpis?')) return;

    try {
      await deleteBlogPost(id);
      setPosts((prev) => prev.filter((p) => p.id !== id));
      if (selectedPost?.id === id) closeModal();
    } catch (err) {
      console.error('Błąd usuwania wpisu:', err);
      alert('Nie udało się usunąć wpisu');
    }
  };

  if (loading) return <Loader />;

  return (
    <>
      <Helmet>
        <html lang="pl" />
        <title>
          Blog - Mikołaj Melnyk | Blog | Artykuły o programowaniu React &
          Node.js
        </title>
        <meta
          name="description"
          content="Artykuły, tutoriale, przemyślenia i notatki z nauki programowania – React, Next.js, Node.js, frontend, backend, TypeScript, Tailwind CSS i dobre praktyki web developmentu. Blog junior web developera."
        />
        <meta
          name="keywords"
          content="blog, programowanie, React, Next.js, Node.js, frontend, backend, TypeScript, Tailwind CSS, web development, tutoriale, artykuły, dobre praktyki, Rzeszów, junior developer"
        />
        <meta
          property="og:title"
          content="Blog - Mikołaj Melnyk | React • Node.js • Web Development"
        />
        <meta
          property="og:description"
          content="Czytaj moje wpisy o React, Node.js, frontendzie i backendzie. Tutoriale, przemyślenia i praktyczne wskazówki z codziennej pracy developera."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://mikolajmelnyk.pl/Blog" />
        <meta property="og:locale" content="pl_PL" />
        <meta property="og:site_name" content="Portfolio Mikołaj Melnyk" />

        <meta name="robots" content="index, follow" />
        <meta name="author" content="Mikołaj Melnyk" />
      </Helmet>

      <div className="blog-container">
        <h1 className="title_h1">Blog</h1>

        <div className="blog-intro ">
          <p>
            W tej sekcji publikuję{' '}
            <span className="accent-text">aktualności</span>,
            <span className="accent-text"> artykuły techniczne</span>,
            <span className="accent-text"> poradniki</span> oraz notatki z nauki
            i pracy nad projektami. Dzielę się tutaj doświadczeniem związanym z
            <span className="accent-text"> frontendem</span>,
            <span className="accent-text"> backendem</span> oraz szeroko pojętym
            <span className="accent-text"> web developmentem</span>.
          </p>
          <p>
            Znajdziesz tu również informacje o nowych projektach, wdrożeniach,
            technologiach, które aktualnie testuję, oraz przemyślenia dotyczące
            <span className="accent-text">
              {' '}
              dobrych praktyk programistycznych
            </span>
            .
          </p>
          <p>
            Jeśli coś Cię zainteresuje lub chcesz podyskutować –
            <strong className="accent-text"> pisz śmiało!</strong>
          </p>
          <Link to="/contact" className="blog-contact-btn">
            Napisz do mnie <i className="fas fa-phone"></i>
          </Link>
        </div>

        {isAuthenticated && (
          <button
            className="add-project-btn"
            onClick={() => openModal(null, true)}
          >
            <i className="fas fa-plus"></i> Dodaj nowy wpis
          </button>
        )}

        <div className="section-blog-posts">
          {posts.length === 0 ? (
            <p>Brak wpisów do wyświetlenia</p>
          ) : (
            posts.map((post) => (
              <div key={post.id} className="blog-card">
                <div className="blog-img">
                  {post.featuredImageUrl ? (
                    <img
                      src={post.featuredImageUrl}
                      alt={post.title}
                      className="blog-main-img"
                    />
                  ) : (
                    <div className="no-image">Brak zdjęcia</div>
                  )}
                  <button
                    onClick={() => openModal(post, false)}
                    className="details-btn"
                  >
                    Czytaj
                  </button>
                </div>

                <div className="blog-text">
                  <div className="blog-title">{post.title}</div>

                  {post.category && (
                    <div className="blog-category">{post.category}</div>
                  )}

                  {post.tags?.length > 0 && (
                    <div className="blog-tags">
                      {post.tags.map((tag) => (
                        <span key={tag}>#{tag}</span>
                      ))}
                    </div>
                  )}

                  <div className="blog-excerpt">{post.excerpt}</div>

                  <div className="blog-meta">
                    <span>{post.author}</span>
                    <span>•</span>
                    <time>
                      {post.publishedAt
                        ? new Date(post.publishedAt).toLocaleDateString(
                            'pl-PL',
                            {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                            }
                          )
                        : 'Szkic'}
                    </time>
                  </div>

                  {isAuthenticated && (
                    <div className="admin-actions">
                      <button
                        onClick={() => openModal(post, true)}
                        className="edit-btn"
                      >
                        Edytuj
                      </button>
                      <button
                        onClick={() => handleDelete(post.id)}
                        className="delete-btn"
                      >
                        Usuń
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {(selectedPost || isEditingMode) && (
        <BlogModal
          post={selectedPost}
          onClose={closeModal}
          isEditing={isEditingMode}
          onSave={handlePostSaved}
          isAuthenticated={isAuthenticated}
        />
      )}
    </>
  );
};

export default Blog;
